import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number | undefined} */
  #fetchTimeout;

  /** @type {number} */
  #requestSequence = 0;

  /** @type {string | null} */
  #latestVariantMediaId = null;

  /** @type {Array<Object> | undefined} */
  #variants;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    if (this.hasAttribute('data-aknes-m12s-variant-picker') && this.dataset.aknesVariantReady === 'true') return;

    super.connectedCallback();
    if (this.hasAttribute('data-aknes-m12s-variant-picker')) this.dataset.aknesVariantReady = 'true';
    this.#cacheOptionInputs();

    this.addEventListener('change', this.variantChanged);
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener('change', this.variantChanged);
    this.#abortController?.abort();
    if (this.#fetchTimeout) clearTimeout(this.#fetchTimeout);
    if (this.hasAttribute('data-aknes-m12s-variant-picker')) delete this.dataset.aknesVariantReady;
  }

  /** Rebuilds input caches after the picker is initialized or morphed. */
  #cacheOptionInputs() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      Array.from(this.querySelectorAll('fieldset[ref="fieldsets[]"]'))
    );
    this.#checkedIndices = [];
    this.#radios = [];

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      this.#checkedIndices.push(initialCheckedIndex === -1 ? [] : [initialCheckedIndex]);
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const startTime = performance.now();

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.#syncLocalOptionAvailability();
    const selectedVariant = this.selectedVariant;
    this.#syncM12sVariantInputs(selectedVariant);
    this.dispatchEvent(
      new VariantSelectedEvent(
        {
          id: selectedOption.dataset.optionValueId ?? '',
        },
        selectedVariant
      )
    );
    this.#updateM12sVariantMedia(selectedVariant);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const useSectionRendering = isOnProductPage && !loadsNewProduct;
    const requestUrl = this.buildRequestUrl(selectedOption, null, [], useSectionRendering);
    this.#scheduleFetchUpdatedSection(requestUrl, morphElementSelector, useSectionRendering ? 180 : 0);

    const url = new URL(window.location.href);

    const variantId = selectedVariant?.id || selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    if (window.Shopify?.designMode) {
      if (this.hasAttribute('data-aknes-m12s-variant-picker')) {
        this.#debugM12sVariant(selectedVariant, performance.now() - startTime);
      } else {
        console.debug('[AKNES Variant] changed', variantId, performance.now() - startTime);
      }
    }
  };

  #syncM12sVariantInputs(variant) {
    if (!this.hasAttribute('data-aknes-m12s-variant-picker')) return;

    const section = this.closest('.shopify-section');
    const productId = this.dataset.productId;
    if (!section || !productId) return;

    const nextVariantId = variant?.id ? String(variant.id) : '';
    const variantInputs = /** @type {NodeListOf<HTMLInputElement>} */ (section.querySelectorAll('input[name="id"]'));

    variantInputs.forEach((input) => {
      const owner = input.closest('[data-product-id]');
      if (!(owner instanceof HTMLElement) || owner.dataset.productId !== productId) return;
      if (input.value === nextVariantId) return;

      input.value = nextVariantId;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  #updateM12sVariantMedia(variant) {
    if (!this.hasAttribute('data-aknes-m12s-variant-picker')) return;

    const requestedVariantId = variant?.id ? String(variant.id) : null;
    this.#latestVariantMediaId = requestedVariantId;
    if (!requestedVariantId) return;

    const section = this.closest('.shopify-section');
    const productId = this.dataset.productId;
    const mediaGallery = Array.from(section?.querySelectorAll('media-gallery') ?? []).find(
      (gallery) => !productId || gallery.dataset.productId === productId
    );
    if (!mediaGallery || this.#latestVariantMediaId !== requestedVariantId) return;

    const selectedOptions = this.selectedOptionValues;
    const selectMedia = () => {
      if (this.#latestVariantMediaId !== requestedVariantId) return;
      if (typeof mediaGallery.selectVariantMedia !== 'function') return;
      mediaGallery.selectVariantMedia(variant, selectedOptions, requestedVariantId);
    };

    if (typeof mediaGallery.selectVariantMedia === 'function') selectMedia();
    else window.customElements.whenDefined('media-gallery').then(selectMedia);
  }

  #debugM12sVariant(variant, duration) {
    const section = this.closest('.shopify-section');
    const productId = this.dataset.productId;
    const formIds = section
      ? Array.from(section.querySelectorAll('input[name="id"]'))
          .filter((input) => input.closest('[data-product-id]')?.dataset.productId === productId)
          .map((input) => input.value)
      : [];

    console.debug('[AKNES Variant Debug]', {
      productHandle: this.closest('[data-aknes-m12s-product-info]')?.dataset.productHandle || '',
      productId,
      optionsCount: this.selectedOptionValues.length,
      selectedOptions: this.selectedOptionValues,
      matchedVariantId: variant?.id || null,
      matchedVariantTitle: variant?.title || null,
      price: variant?.price ?? null,
      compareAtPrice: variant?.compare_at_price ?? null,
      available: variant?.available ?? false,
      formIds,
      loadedScripts: {
        variantPicker: Array.from(document.scripts).some((script) => script.src.includes('/variant-picker.js')),
        productForm: Array.from(document.scripts).some((script) => script.src.includes('/product-form.js')),
        productPrice: Array.from(document.scripts).some((script) => script.src.includes('/product-price.js')),
        mediaGallery: Array.from(document.scripts).some((script) => script.src.includes('/media-gallery.js')),
      },
      duration,
    });
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  #syncLocalOptionAvailability() {
    if (
      this.hasAttribute('data-aknes-m12s-variant-picker') ||
      this.querySelector('[data-connected-product-url]:not([data-connected-product-url=""])')
    ) {
      return;
    }

    const variants = this.variants;
    if (!variants.length) return;

    const optionGroups = Array.from(
      this.querySelectorAll(
        'fieldset[data-option-position], select[data-option-position], [data-selected-option-value][data-option-position]'
      )
    ).sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
    const selectedValues = this.selectedOptionValues;

    optionGroups.forEach((group) => {
      const optionPosition = Number(group.dataset.optionPosition);
      if (!Number.isInteger(optionPosition) || optionPosition < 1) return;

      const choices =
        group instanceof HTMLFieldSetElement
          ? Array.from(group.querySelectorAll('input[data-option-value]'))
          : group instanceof HTMLSelectElement
          ? Array.from(group.options)
          : [];

      choices.forEach((choice) => {
        if (!(choice instanceof HTMLInputElement || choice instanceof HTMLOptionElement)) return;

        const optionValue = choice.dataset.optionValue || choice.value;
        const available = variants.some((variant) => {
          if (variant.available !== true) return false;

          const variantOptions = Array.isArray(variant.options)
            ? variant.options
            : [variant.option1, variant.option2, variant.option3].filter((value) => value != null);

          if (String(variantOptions[optionPosition - 1]) !== optionValue) return false;

          return selectedValues
            .slice(0, optionPosition - 1)
            .every((value, index) => String(variantOptions[index]) === value);
        });

        this.#setOptionAvailability(choice, available);
      });
    });
  }

  #setOptionAvailability(choice, available) {
    const availableValue = String(available);
    choice.dataset.optionAvailable = availableValue;
    if (available) choice.removeAttribute('aria-disabled');
    else choice.setAttribute('aria-disabled', 'true');

    const optionLabel = choice.dataset.optionLabel || choice.dataset.optionValue || choice.value;

    if (choice instanceof HTMLOptionElement) {
      choice.textContent = available ? optionLabel : `${optionLabel} - ${this.dataset.unavailableText || ''}`.trim();
      return;
    }

    choice.setAttribute(
      'aria-label',
      available ? optionLabel : `${optionLabel} - ${this.dataset.soldOutText || ''}`.trim()
    );

    const label = choice.closest('label');
    if (!label) return;

    let pill = label.querySelector('[data-key="variant-option-pill"]');
    if (available && !pill && !label.classList.contains('variant-option__button-label--has-swatch')) {
      pill = document.createElement('span');
      pill.className = 'variant-option__button-label__pill';
      pill.setAttribute('data-key', 'variant-option-pill');
      label.insertBefore(pill, label.querySelector('[data-key="variant-option-text"]'));
    }
    pill?.toggleAttribute('hidden', !available);

    let strikethrough = label.querySelector('.variant-option__strikethrough');
    if (!available && !strikethrough) {
      const svgNamespace = 'http://www.w3.org/2000/svg';
      strikethrough = document.createElementNS(svgNamespace, 'svg');
      strikethrough.classList.add('variant-option__strikethrough');
      strikethrough.setAttribute('viewBox', '0 0 100 46');
      strikethrough.setAttribute('preserveAspectRatio', 'xMidYMid slice');
      strikethrough.setAttribute('aria-hidden', 'true');

      for (let index = 0; index < 2; index++) {
        const line = document.createElementNS(svgNamespace, 'line');
        line.setAttribute('x1', '100');
        line.setAttribute('y1', '0');
        line.setAttribute('x2', '0');
        line.setAttribute('y2', '46');
        line.setAttribute('vector-effect', 'non-scaling-stroke');
        strikethrough.append(line);
      }

      label.append(strikethrough);
    }
    strikethrough?.toggleAttribute('hidden', available);
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {boolean} [useSectionRendering] - Request only the current product section.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], useSectionRendering = false) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (useSectionRendering && this.dataset.sectionId) {
      params.push(`section_id=${encodeURIComponent(this.dataset.sectionId)}`);
    }

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    if (useSectionRendering && productUrl?.includes('?')) {
      productUrl = productUrl.split('?')[0];
    }

    const separator = productUrl?.includes('?') ? '&' : '?';
    return `${productUrl}${separator}${params.join('&')}`;
  }

  /**
   * Debounces supplementary section requests while keeping local variant UI synchronous.
   * @param {string} requestUrl
   * @param {string} [morphElementSelector]
   * @param {number} [delay]
   */
  #scheduleFetchUpdatedSection(requestUrl, morphElementSelector, delay = 0) {
    if (this.#fetchTimeout) clearTimeout(this.#fetchTimeout);
    this.#abortController?.abort();

    if (delay <= 0) {
      this.fetchUpdatedSection(requestUrl, morphElementSelector);
      return;
    }

    this.#fetchTimeout = setTimeout(() => {
      this.#fetchTimeout = undefined;
      this.fetchUpdatedSection(requestUrl, morphElementSelector);
    }, delay);
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const requestSequence = ++this.#requestSequence;

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        if (requestSequence !== this.#requestSequence || this.#abortController?.signal.aborted) return;
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else if (!this.hasAttribute('data-aknes-m12s-variant-picker')) {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#variants = undefined;
    this.#cacheOptionInputs();
    queueMicrotask(() => this.updateVariantPickerCss());

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked, [data-selected-option-value-id]')
    );

    return selectedOptions.map((option) => {
      const optionValueId = option.dataset.optionValueId || option.dataset.selectedOptionValueId;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  /**
   * Gets selected option values in Shopify option position order.
   * Includes one-value option groups that do not render an input.
   * @returns {string[]}
   */
  get selectedOptionValues() {
    const selector = this.hasAttribute('data-aknes-m12s-variant-picker')
      ? [
          'fieldset[data-option-position]',
          'select[data-option-position]',
          '[data-selected-option-value][data-option-position]',
        ].join(', ')
      : '[data-option-position]';
    const optionGroups = Array.from(this.querySelectorAll(selector)).sort(
      (a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
    );

    return optionGroups
      .map((group) => {
        if (group instanceof HTMLSelectElement) return group.value;
        if (group instanceof HTMLFieldSetElement) {
          return /** @type {HTMLInputElement | null} */ (group.querySelector('input:checked'))?.value || '';
        }
        return group.dataset.selectedOptionValue || '';
      })
      .filter(Boolean);
  }

  /** @returns {Array<Object>} */
  get variants() {
    if (this.#variants) return this.#variants;

    const productInfo = this.closest('[data-aknes-m12s-product-info]');
    const script = this.hasAttribute('data-aknes-m12s-variant-picker')
      ? productInfo?.querySelector('script[data-aknes-product-json]')
      : this.querySelector('script[data-product-variants]');
    if (!script?.textContent) return [];

    try {
      const productData = JSON.parse(script.textContent);
      const variants = Array.isArray(productData) ? productData : productData?.variants;
      this.#variants = Array.isArray(variants) ? variants : [];
    } catch (_error) {
      this.#variants = [];
    }

    return this.#variants;
  }

  /** @returns {Object | null} */
  get selectedVariant() {
    const selectedOptions = this.selectedOptionValues;
    if (!selectedOptions.length) return null;

    return (
      this.variants.find((variant) => {
        const variantOptions = Array.isArray(variant.options)
          ? variant.options
          : [variant.option1, variant.option2, variant.option3].filter((value) => value != null);

        return (
          variantOptions.length === selectedOptions.length &&
          variantOptions.every((value, index) => String(value) === selectedOptions[index])
        );
      }) || null
    );
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
